import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  BULK_APPLY_SHORTLISTED,
  JOB_ORDER_REC_SEARCH_SETTINGS,
  REC_ACTIVE_JOB_ORDERS,
  REC_COMPANIES_DROPDOWN,
  RECSYS_DISPOSITIONS_FOR_JOB,
  REMOVE_RECSYS_DISPOSITION,
  RESOLVE_LOCATION,
  SET_RECSYS_DISPOSITION,
  UPSERT_JOB_ORDER_REC_SEARCH_SETTINGS,
} from './gql';
import {
  buildSearchSettingsPayload,
  DEFAULT_FILTERS,
  MAX_RADIUS_MI,
  sanitizeSavedSearchSettings,
  stripHtml,
} from './utils';
import {
  FilterState,
  GeoResolveStatus,
  PoolInfo,
  PoolScope,
  RecPageState,
  RecsysDisposition,
  RecsysIngestStatus,
  ResolvedLocation,
  SearchMode,
  TabKey,
} from './types';

/**
 * Shared page state for the Candidate Recommendations feature. This is the
 * seam every Stage-1 component's data flows through: index.tsx is the sole
 * consumer of useRecPage() and maps slices of this context onto the exact
 * prop contracts declared in ./types.ts (FiltersRailProps, ResultsPanelProps,
 * etc.) — Stage-1 components stay plain, prop-driven, and easy to test in
 * isolation. SelectionPanel/TabBar (this lane) consume the context directly.
 */

export interface RecCompanyOption {
  id: string;
  name: string;
}

export interface RecActiveJobOrder {
  id: string;
  jobTitle: string;
  advertisedJobDescription: string | null;
  status: string;
  createdAt: string;
  newApplications: number;
  totalApplications: number;
  category: { id: string; name: string } | null;
  companyLocation: {
    city: string | null;
    state: string | null;
    country: string | null;
    latitude: string | null;
    longitude: string | null;
  } | null;
  company: { id: string; name: string };
}

// FilterState.radiusMi sentinel meaning "Any distance" (README: 10-250mi
// slider). Canonically defined in utils.ts (next to buildRecsysFilters, which
// consumes it); re-exported here for the page's components/tests.
export { MAX_RADIUS_MI };

// Default radius applied together with the selected job order's own location
// (kan-78 item 1): picking a job pre-filters the results to candidates within
// 20 miles of that job, which the recruiter can widen/clear from the rail.
export const DEFAULT_JOB_RADIUS_MI = 20;

// Empty filter state. Canonically defined in utils.ts (next to the saved-
// settings sanitizer, which falls back to it); re-exported here for the page's
// components/tests — same arrangement as MAX_RADIUS_MI above.
export { DEFAULT_FILTERS };

// Longest location text sent to resolveLocation; the backend caps at the same
// length, so slicing here just keeps the recruiter from ever seeing a 400.
const MAX_GEO_CENTER_CHARS = 120;

/**
 * The geo slice of FilterState a job order's STORED company coordinates imply:
 * that location as the center + DEFAULT_JOB_RADIUS_MI. Since kan-83 these are
 * no longer the source of truth — the location box's text is, resolved through
 * the ATS location service (see resolveJobPrefill / commitGeoCenter below).
 * They remain the instant center shown while the first resolution is in flight,
 * and the silent fallback when a resolution CALL fails. No job / no location at
 * all clears the geo filter entirely.
 */
// Missing/blank coordinate strings parse to NaN (never 0 — Number('') === 0,
// and a literal "0" latitude/longitude is a valid coordinate, not "unset").
const parseCoordinate = (raw: string | null): number =>
  raw != null && raw.trim() !== '' ? Number(raw) : NaN;

const jobGeoDefaults = (
  job: RecActiveJobOrder | null
): Pick<FilterState, 'lat' | 'lng' | 'geoCenterLabel' | 'radiusMi'> => {
  const location = job?.companyLocation;
  if (!location) return { lat: null, lng: null, geoCenterLabel: '', radiusMi: MAX_RADIUS_MI };

  const lat = parseCoordinate(location.latitude);
  const lng = parseCoordinate(location.longitude);
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
  return {
    lat: hasCoordinates ? lat : null,
    lng: hasCoordinates ? lng : null,
    geoCenterLabel: [location.city, location.state].filter(Boolean).join(', '),
    radiusMi: hasCoordinates ? DEFAULT_JOB_RADIUS_MI : MAX_RADIUS_MI,
  };
};

const INITIAL_STATE: RecPageState = {
  view: 'batch',
  selectedCompanyId: null,
  jobId: null,
  jobDescription: '',
  jobCenter: { lat: null, lng: null, label: '' },
  topN: 20,
  mustHave: [],
  filters: DEFAULT_FILTERS,
  search: { mode: 'hybrid', query: '' },
  page: 1,
  detailId: null,
  resumeViewId: null,
  ingest: null,
};

export interface RecPageContextValue extends RecPageState {
  setView: (view: TabKey) => void;
  /** Selects a client company; resets job/page and lets the auto-select-first-job effect pick a job. */
  selectCompany: (companyId: string | null) => void;
  /** Manually selects an active job order within the current company. */
  selectJob: (jobId: string | null) => void;
  setTopN: (n: number) => void;
  setMustHave: (terms: string[]) => void;
  setFilters: (patch: Partial<FilterState>) => void;
  /** Increments only on recruiter-initiated filter edits (setFilters/clearFilters),
   *  never on programmatic applies — the search auto-rerun keys on this. */
  filterEditCount: number;
  clearFilters: () => void;

  /**
   * Resolves the location box's text to the radius filter's center (kan-83).
   * Called by the rail on BLUR / ENTER only — never per keystroke, since every
   * commit can cost a geocode. Empty text clears the geo filter without calling
   * the service. Last commit issued wins; a response that a newer commit or a
   * job switch has superseded is dropped.
   */
  commitGeoCenter: (text: string) => void;
  /** Status + label of the current center resolution (see types.GeoResolveStatus). */
  geoResolve: { status: GeoResolveStatus; formattedAddress: string | null };
  setSearchMode: (mode: SearchMode) => void;
  setSearchQuery: (query: string) => void;
  setPage: (page: number) => void;
  setDetailId: (id: string | null) => void;
  setResumeViewId: (id: string | null) => void;
  setIngest: (status: RecsysIngestStatus | null) => void;

  /**
   * Saves the page's complete search configuration against the selected job
   * order (kan-80) — called by index.tsx at the start of every executed search
   * in either mode. Fire-and-forget: never blocks, never toasts, last write
   * wins. No-ops without a selected job order.
   */
  persistSearchSettings: (mode: 'batch' | 'search') => void;

  /**
   * "Search in" scope (kan-119): 'all' = the whole corpus, 'applicants' = the
   * selected job order's new (Applied) applicants only — applies to all three
   * pool tabs. Session-only (never part of the kan-80 saved settings) and reset
   * to 'all' whenever the job order changes. index.tsx treats a scope change
   * like a job change (clears results, awaits the tab's run button).
   */
  poolScope: PoolScope;
  setPoolScope: (scope: PoolScope) => void;
  /** What the last applicant-mode run reported ("N of M applicants
   *  searchable"). null before a run, and cleared on every job/scope change. */
  poolInfo: PoolInfo | null;
  setPoolInfo: (info: PoolInfo | null) => void;

  // Selection-panel data (company -> active job order dropdowns).
  companies: RecCompanyOption[];
  companiesLoading: boolean;
  jobs: RecActiveJobOrder[];
  jobsLoading: boolean;
  activeJob: RecActiveJobOrder | null;

  // Story 2b dispositions (Shortlist / Decline persistence).
  dispositions: RecsysDisposition[];
  shortlistIds: string[];
  declinedIds: string[];
  shortlistCount: number;
  declinedCount: number;
  refetchDispositions: () => void;
  shortlist: (candidateId: string) => Promise<void>;
  decline: (candidateId: string) => Promise<void>;
  restore: (candidateId: string) => Promise<void>;
  bulkApply: (candidateIds: string[]) => Promise<void>;
}

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = (): void => {};
const noopAsync = async (): Promise<void> => {};
/* eslint-enable @typescript-eslint/no-empty-function */

const defaultContextValue: RecPageContextValue = {
  ...INITIAL_STATE,
  setView: noop,
  selectCompany: noop,
  selectJob: noop,
  setTopN: noop,
  setMustHave: noop,
  setFilters: noop,
  filterEditCount: 0,
  clearFilters: noop,
  commitGeoCenter: noop,
  geoResolve: { status: 'idle', formattedAddress: null },
  setSearchMode: noop,
  setSearchQuery: noop,
  setPage: noop,
  setDetailId: noop,
  setResumeViewId: noop,
  setIngest: noop,
  persistSearchSettings: noop,
  poolScope: 'all',
  setPoolScope: noop,
  poolInfo: null,
  setPoolInfo: noop,
  companies: [],
  companiesLoading: false,
  jobs: [],
  jobsLoading: false,
  activeJob: null,

  dispositions: [],
  shortlistIds: [],
  declinedIds: [],
  shortlistCount: 0,
  declinedCount: 0,
  refetchDispositions: noop,
  shortlist: noopAsync,
  decline: noopAsync,
  restore: noopAsync,
  bulkApply: noopAsync,
};

export const RecPageContext = createContext<RecPageContextValue>(defaultContextValue);

export const useRecPage = (): RecPageContextValue => useContext(RecPageContext);

// Persist the recruiter's company/job selection across browser refreshes.
// Stored per-page (not per-user) in localStorage, matching the app's existing
// localStorage conventions; stale ids are validated against the fetched lists
// below and silently reset when no longer available.
const STORAGE_COMPANY_KEY = 'candidateSearch.companyId';
const STORAGE_JOB_KEY = 'candidateSearch.jobId';

const readStoredId = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

const writeStoredId = (key: string, value: string | null): void => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — selection just won't survive refresh */
  }
};

export const RecPageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [view, setView] = useState<TabKey>(INITIAL_STATE.view);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() =>
    readStoredId(STORAGE_COMPANY_KEY)
  );
  const [jobId, setJobIdState] = useState<string | null>(() => readStoredId(STORAGE_JOB_KEY));
  const [topN, setTopN] = useState<number>(INITIAL_STATE.topN);
  const [mustHave, setMustHave] = useState<string[]>([]);
  const [filters, setFiltersState] = useState<FilterState>(DEFAULT_FILTERS);
  // Recruiter-initiated filter-edit counter — see the setFilters comment below.
  const [filterEditCount, setFilterEditCount] = useState(0);
  const [search, setSearch] = useState<{ mode: SearchMode; query: string }>(INITIAL_STATE.search);
  const [page, setPage] = useState<number>(1);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [resumeViewId, setResumeViewId] = useState<string | null>(null);
  const [ingest, setIngest] = useState<RecsysIngestStatus | null>(null);
  // kan-119 "Search in" scope + the last applicant-mode run's pool numbers.
  // Deliberately outside every kan-80 path: not persisted, not restored, and
  // not touched by cancelSavedSettingsApply.
  const [poolScope, setPoolScopeState] = useState<PoolScope>('all');
  const [poolInfo, setPoolInfo] = useState<PoolInfo | null>(null);
  // Last job order the geo filter was prefilled from — drives "the recruiter
  // switched jobs" in the prefill effect below (see its comment).
  const prevJobIdRef = useRef<string | null>(null);
  // kan-80 saved-settings load (see the loader effect below). Deliberately NOT
  // prevJobIdRef, which belongs to the geo-prefill effect:
  //  - settingsJobIdRef: last job order the loader fired for (fetch once per job)
  //  - settingsFetchJobIdRef: the pending-apply token — cleared the moment the
  //    recruiter takes over (executes a search / edits a persisted field), so a
  //    late response can never yank state out from under them
  //  - currentJobIdRef: the job order selected right now, so a response that
  //    arrives after a job switch is dropped
  const settingsJobIdRef = useRef<string | null>(null);
  const settingsFetchJobIdRef = useRef<string | null>(null);
  const currentJobIdRef = useRef<string | null>(null);
  currentJobIdRef.current = jobId;

  // kan-83 radius-center resolution. Session-only — never persisted (it is not
  // part of FilterState, so buildSearchSettingsPayload can't pick it up).
  const [geoResolve, setGeoResolve] = useState<{
    status: GeoResolveStatus;
    formattedAddress: string | null;
  }>({ status: 'idle', formattedAddress: null });
  //  - geoResolveSeqRef: last-issued-wins token (same discipline as index.tsx's
  //    fetchTokenRef). Bumped by every commit AND by every state transition that
  //    invalidates an in-flight resolution (saved-settings apply, job switch,
  //    Clear all), so a slow response can never land on top of newer state.
  //  - lastCommitTextRef: the text the last issued resolution was for — blur
  //    after Enter (and idempotent re-blurs) must not re-spend a geocode.
  //  - resolvedPrefillRef: the service-resolved center for the job's own label,
  //    so the job-prefill effect below re-applies THAT rather than reverting to
  //    the job's stored coordinates when a custom flag flips.
  const geoResolveSeqRef = useRef(0);
  const geoResolveRef = useRef(geoResolve);
  geoResolveRef.current = geoResolve;
  const lastCommitTextRef = useRef<string | null>(null);
  const resolvedPrefillRef = useRef<{
    jobId: string;
    lat: number;
    lng: number;
    formattedAddress: string | null;
  } | null>(null);
  // Read by the async resolution callbacks, which must never act on the filter
  // state captured when the request was issued.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const apolloClient = useApolloClient();

  const { data: companiesData, loading: companiesLoading } = useQuery(REC_COMPANIES_DROPDOWN, {
    variables: { page: 1, limit: 200, searchText: '' },
    fetchPolicy: 'cache-and-network',
  });
  // Memoized like `jobs` below — the restored-selection validation effect
  // depends on this array, so its reference must be stable across renders.
  const companies: RecCompanyOption[] = useMemo(
    () => companiesData?.fetchCompaniesOfCurrentUser ?? [],
    [companiesData]
  );

  const { data: jobsData, loading: jobsLoading } = useQuery(REC_ACTIVE_JOB_ORDERS, {
    variables: { page: 1, limit: 200, companyId: selectedCompanyId, statusSearch: 'ACTIVE' },
    skip: !selectedCompanyId,
    fetchPolicy: 'cache-and-network',
  });
  // Memoized so its reference is stable across renders — the auto-select
  // effect and activeJob memo below depend on `jobs`, and a fresh array each
  // render would re-run them every render (and trips react-hooks/exhaustive-deps,
  // which CI promotes to a build error).
  const jobs: RecActiveJobOrder[] = useMemo(
    () =>
      selectedCompanyId
        ? [...(jobsData?.fetchAllJobOrders?.jobOrders ?? [])].sort(
            (a, b) =>
              (a.jobTitle ?? '').localeCompare(b.jobTitle ?? '', undefined, {
                sensitivity: 'base',
              }) || a.id.localeCompare(b.id)
          )
        : [],
    [selectedCompanyId, jobsData]
  );

  const activeJob = useMemo(() => jobs.find((j) => j.id === jobId) ?? null, [jobs, jobId]);
  const activeJobRef = useRef<RecActiveJobOrder | null>(activeJob);
  activeJobRef.current = activeJob;

  /**
   * jobGeoDefaults overlaid with the service-resolved center for this job's own
   * label, once it has arrived. Every path that rebuilds the geo slice from the
   * job (the prefill effect, the job-switch reset, Clear all) goes through this
   * — otherwise merely moving the radius slider would re-run the prefill effect
   * and silently revert a resolved center to the job's stored coordinates.
   */
  const jobGeoDefaultsResolved = (
    job: RecActiveJobOrder | null
  ): Pick<FilterState, 'lat' | 'lng' | 'geoCenterLabel' | 'radiusMi'> => {
    const base = jobGeoDefaults(job);
    const resolved = resolvedPrefillRef.current;
    if (!job || !resolved || resolved.jobId !== job.id) return base;
    return { ...base, lat: resolved.lat, lng: resolved.lng, radiusMi: DEFAULT_JOB_RADIUS_MI };
  };

  /**
   * Resolves the SELECTED JOB's own city/state through the location service
   * (kan-83), refining the stored-coordinate prefill that has already been
   * applied synchronously. Programmatic: it never bumps filterEditCount (that
   * would auto-fire a search on job selection) and never overrides a center the
   * recruiter has started editing.
   *
   * Called only once the saved-settings read has concluded WITHOUT applying a
   * row — a restored row's own center is trusted as stored (kan-80) and must not
   * be re-resolved.
   */
  const resolveJobPrefill = (job: RecActiveJobOrder | null): void => {
    if (!job) return;
    const location = job.companyLocation;
    const label = location ? [location.city, location.state].filter(Boolean).join(', ') : '';
    const seq = ++geoResolveSeqRef.current;
    if (!label) {
      // Nothing to resolve: report whatever the synchronous prefill left applied.
      const base = jobGeoDefaults(job);
      setGeoResolve({ status: base.lat === null ? 'idle' : 'resolved', formattedAddress: null });
      return;
    }
    lastCommitTextRef.current = label;
    setGeoResolve({ status: 'resolving', formattedAddress: null });

    const jobAtIssue = job.id;
    const isStale = (): boolean =>
      geoResolveSeqRef.current !== seq || currentJobIdRef.current !== jobAtIssue;

    apolloClient
      .query<{ resolveLocation: ResolvedLocation | null }>({
        query: RESOLVE_LOCATION,
        variables: { locationText: label.slice(0, MAX_GEO_CENTER_CHARS) },
      })
      .then((res) => {
        if (isStale()) return;
        // The recruiter typed while we resolved: the box's text owns the center.
        // Their blur/Enter commit issues its own resolution — but the status
        // must still settle off the coordinates actually applied, or the slider
        // stays disabled on a 'resolving' nothing else will ever clear.
        if (filtersRef.current.geoCenterCustom) {
          setGeoResolve({
            status: filtersRef.current.lat === null ? 'idle' : 'resolved',
            formattedAddress: null,
          });
          return;
        }
        const found = res.data?.resolveLocation ?? null;
        if (!found) {
          setFiltersState((prev) =>
            prev.geoCenterCustom ? prev : { ...prev, lat: null, lng: null, radiusMi: MAX_RADIUS_MI }
          );
          setGeoResolve({ status: 'noMatch', formattedAddress: null });
          return;
        }
        resolvedPrefillRef.current = {
          jobId: jobAtIssue,
          lat: found.latitude,
          lng: found.longitude,
          formattedAddress: found.formattedAddress,
        };
        setFiltersState((prev) =>
          prev.geoCenterCustom
            ? prev
            : {
                ...prev,
                lat: found.latitude,
                lng: found.longitude,
                radiusMi: prev.radiusCustom ? prev.radiusMi : DEFAULT_JOB_RADIUS_MI,
              }
        );
        setGeoResolve({ status: 'resolved', formattedAddress: found.formattedAddress });
      })
      .catch(() => {
        // The call failed (not "no match"): the job's stored coordinates are
        // already applied, which is exactly the pre-kan-83 behavior.
        if (isStale()) return;
        setGeoResolve({
          status: filtersRef.current.lat === null ? 'error' : 'resolved',
          formattedAddress: null,
        });
      });
  };

  // Story 2b dispositions: one query per selected job, job-wide (not
  // per-recruiter) — mirrors the job-wide applied-candidate exclusion. Every
  // mutation below refetches this so both tab counts and tab lists stay in
  // sync.
  const {
    data: dispositionsData,
    refetch: refetchDispositionsQuery,
  } = useQuery(RECSYS_DISPOSITIONS_FOR_JOB, {
    variables: { jobOrderId: jobId },
    skip: !jobId,
    fetchPolicy: 'cache-and-network',
  });
  const dispositions: RecsysDisposition[] = useMemo(
    () => dispositionsData?.recsysDispositionsForJob ?? [],
    [dispositionsData]
  );
  const shortlistIds = useMemo(
    () => dispositions.filter((d) => d.status === 'SHORTLISTED').map((d) => d.candidateId),
    [dispositions]
  );
  const declinedIds = useMemo(
    () => dispositions.filter((d) => d.status === 'DECLINED').map((d) => d.candidateId),
    [dispositions]
  );

  const refetchDispositions = useCallback((): void => {
    if (!jobId) return;
    refetchDispositionsQuery().catch(() => {
      /* cache-and-network already surfaces stale data; swallow the refetch error */
    });
  }, [jobId, refetchDispositionsQuery]);

  const [setRecsysDispositionMutation] = useMutation(SET_RECSYS_DISPOSITION);
  const [removeRecsysDispositionMutation] = useMutation(REMOVE_RECSYS_DISPOSITION);
  const [bulkApplyShortlistedMutation] = useMutation(BULK_APPLY_SHORTLISTED);
  const [upsertSearchSettingsMutation] = useMutation(UPSERT_JOB_ORDER_REC_SEARCH_SETTINGS);

  const shortlist = useCallback(
    async (candidateId: string): Promise<void> => {
      if (!jobId) return;
      try {
        await setRecsysDispositionMutation({
          variables: { jobOrderId: jobId, candidateId, status: 'SHORTLISTED' },
        });
        refetchDispositions();
      } catch (err) {
        toast.error('Could not add candidate to shortlist. Please try again.');
        throw err;
      }
    },
    [jobId, setRecsysDispositionMutation, refetchDispositions]
  );

  const decline = useCallback(
    async (candidateId: string): Promise<void> => {
      if (!jobId) return;
      try {
        await setRecsysDispositionMutation({
          variables: { jobOrderId: jobId, candidateId, status: 'DECLINED' },
        });
        refetchDispositions();
      } catch (err) {
        toast.error('Could not decline candidate. Please try again.');
        throw err;
      }
    },
    [jobId, setRecsysDispositionMutation, refetchDispositions]
  );

  const restore = useCallback(
    async (candidateId: string): Promise<void> => {
      if (!jobId) return;
      try {
        await removeRecsysDispositionMutation({ variables: { jobOrderId: jobId, candidateId } });
        refetchDispositions();
      } catch (err) {
        toast.error('Could not restore candidate. Please try again.');
        throw err;
      }
    },
    [jobId, removeRecsysDispositionMutation, refetchDispositions]
  );

  const bulkApply = useCallback(
    async (candidateIds: string[]): Promise<void> => {
      if (!jobId || !candidateIds.length) return;
      try {
        const res = await bulkApplyShortlistedMutation({
          variables: { jobOrderId: jobId, candidateIds },
        });
        refetchDispositions();
        // bulkApplyShortlisted is non-transactional and returns HTTP 200 even on
        // partial failure ("Applied N of M candidate(s), K failed"). Surface that
        // message so a recruiter never sees candidates silently reappear after
        // the optimistic drop + refetch.
        const message: string = res?.data?.bulkApplyShortlisted?.message ?? '';
        if (/failed/i.test(message)) {
          toast.warn(message || 'Some candidates could not be applied.');
        } else {
          toast.success(message || 'Candidate(s) applied.');
        }
      } catch (err) {
        toast.error('Could not apply the selected candidates. Please try again.');
        throw err;
      }
    },
    [jobId, bulkApplyShortlistedMutation, refetchDispositions]
  );

  // Persist the selection so a browser refresh restores the same company/job.
  // Covers both explicit picks and the auto-selected first job below.
  useEffect(() => {
    writeStoredId(STORAGE_COMPANY_KEY, selectedCompanyId);
  }, [selectedCompanyId]);
  useEffect(() => {
    writeStoredId(STORAGE_JOB_KEY, jobId);
  }, [jobId]);

  // Validate a restored company id once the (user-scoped, active-job-only)
  // company list arrives: if it's no longer in the list — access revoked, job
  // closed, different user on this browser — clear the selection entirely.
  useEffect(() => {
    if (companiesLoading || !selectedCompanyId || !companies.length) return;
    if (!companies.some((c) => c.id === selectedCompanyId)) {
      setSelectedCompanyId(null);
      setJobIdState(null);
    }
  }, [companiesLoading, companies, selectedCompanyId]);

  // Auto-select the company's first active job whenever its job list loads
  // and no job is currently selected. selectCompany() clears jobId first, so
  // this effect is what performs "company change -> reset job to that
  // company's first" (README §Interactions & Behavior). A restored-but-stale
  // jobId (job closed since last visit) also falls back to the first job.
  useEffect(() => {
    if (!selectedCompanyId || jobsLoading || !jobs.length) return;
    if (!jobId) {
      setJobIdState(jobs[0].id);
    } else if (!jobs.some((j) => j.id === jobId)) {
      setJobIdState(jobs[0].id);
    }
  }, [selectedCompanyId, jobId, jobsLoading, jobs]);

  // Prefill the geo filter from the active job: its company location as the
  // center at DEFAULT_JOB_RADIUS_MI (kan-78 item 1). Runs whenever the active
  // job changes (covers both the auto-picked first job and a manual job
  // change). Selecting a DIFFERENT job drops the recruiter's manual
  // center/radius and re-prefills; while the same job stays selected a manual
  // edit (geoCenterCustom / radiusCustom) is never clobbered — including by a
  // background refetch of the job list, which is why the ref only ever tracks
  // a non-null job id.
  // The center comes from jobGeoDefaultsResolved, not jobGeoDefaults: this
  // effect also re-runs on a custom-flag flip, and must re-apply the resolved
  // center (kan-83) rather than the job's stored coordinates.
  useEffect(() => {
    const nextJobId = activeJob?.id ?? null;
    // No job selected (company switch in flight, list still loading): leave the
    // recruiter's filters alone — the job-order effects above pick a job next.
    if (!nextJobId) return;

    const jobChanged = prevJobIdRef.current !== nextJobId;
    prevJobIdRef.current = nextJobId;

    const next = jobGeoDefaultsResolved(activeJob);

    // The label this prefill puts in the box is the job's own, not a recruiter
    // edit: a no-edit blur over it must not re-resolve it (or cancel a pending
    // saved-settings apply). Only on a job change — while the same job stays
    // selected the ref belongs to the commit/restore paths.
    if (jobChanged) lastCommitTextRef.current = next.geoCenterLabel;

    setFiltersState((prev) => {
      const keepCenter = !jobChanged && prev.geoCenterCustom;
      const keepRadius = !jobChanged && prev.radiusCustom;
      const merged: FilterState = {
        ...prev,
        lat: keepCenter ? prev.lat : next.lat,
        lng: keepCenter ? prev.lng : next.lng,
        geoCenterLabel: keepCenter ? prev.geoCenterLabel : next.geoCenterLabel,
        radiusMi: keepRadius ? prev.radiusMi : next.radiusMi,
        geoCenterCustom: jobChanged ? false : prev.geoCenterCustom,
        radiusCustom: jobChanged ? false : prev.radiusCustom,
      };
      if (
        merged.lat === prev.lat &&
        merged.lng === prev.lng &&
        merged.geoCenterLabel === prev.geoCenterLabel &&
        merged.radiusMi === prev.radiusMi &&
        merged.geoCenterCustom === prev.geoCenterCustom &&
        merged.radiusCustom === prev.radiusCustom
      ) {
        return prev;
      }
      return merged;
    });
    // filters.geoCenterCustom / filters.radiusCustom are read (not just deps)
    // to re-run when the recruiter clears a custom center or radius back to the
    // job's default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id, filters.geoCenterCustom, filters.radiusCustom]);

  /**
   * Cancels a pending saved-settings apply (kan-80). Called whenever the
   * recruiter takes over the configuration — executing a search, or editing any
   * field that gets persisted — so an in-flight load (which may predate their
   * change) can never overwrite what they just did.
   */
  const cancelSavedSettingsApply = (): void => {
    settingsFetchJobIdRef.current = null;
  };

  // Load the job order's saved search configuration (kan-80). Runs once per
  // selected job, AFTER the geo prefill above has already applied
  // synchronously: a saved row therefore takes precedence over the job-derived
  // default, and a job that was never searched simply keeps that default.
  // network-only on purpose — the row is shared and overwritten by other users,
  // so a cached read could serve a superseded configuration.
  useEffect(() => {
    const nextJobId = activeJob?.id ?? null;
    if (!nextJobId || settingsJobIdRef.current === nextJobId) return;
    const jobSwitched = settingsJobIdRef.current !== null;
    settingsJobIdRef.current = nextJobId;
    settingsFetchJobIdRef.current = nextJobId;

    // Switching jobs resets the whole search configuration to page defaults
    // SYNCHRONOUSLY, before the saved-row read below resolves. The new job must
    // never start from (or, worse, persist) the previous job's configuration —
    // whatever the read turns out to hold: a row (it applies over this reset),
    // no row, an unreadable row, or a failed request. Without this, the first
    // search on a never-searched job would silently write the previous job's
    // strategy onto its shared row. Geo is rebuilt from the job (kan-78
    // prefill semantics) in the same state transition so the reset and the
    // prefill can't interleave. The very first selection is exempt: state is
    // already at defaults, and anything typed before a job existed survives.
    if (jobSwitched) {
      // Any in-flight center resolution belonged to the previous job.
      geoResolveSeqRef.current += 1;
      resolvedPrefillRef.current = null;
      const geo = jobGeoDefaultsResolved(activeJob);
      // The label now in the box is the job's own, not a recruiter edit: a
      // no-edit blur over it must not re-spend a geocode or count as an edit.
      lastCommitTextRef.current = geo.geoCenterLabel;
      setFiltersState({ ...DEFAULT_FILTERS, ...geo });
      setMustHave([]);
      setTopN(INITIAL_STATE.topN);
      setSearch({ ...INITIAL_STATE.search });
      setPage(1);
      // Stored coordinates hold the center until this job's own resolution is
      // issued below; 'idle' (not 'noMatch') because nothing has been tried yet.
      setGeoResolve({ status: geo.lat === null ? 'idle' : 'resolved', formattedAddress: null });
    }

    apolloClient
      .query<{ jobOrderRecSearchSettings: { settings: unknown } | null }>({
        query: JOB_ORDER_REC_SEARCH_SETTINGS,
        variables: { jobOrderId: nextJobId },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        // Superseded by a newer selection, or the recruiter has taken over.
        if (settingsFetchJobIdRef.current !== nextJobId || currentJobIdRef.current !== nextJobId) {
          return;
        }
        settingsFetchJobIdRef.current = null;

        // Never searched, or an unreadable row: keep the starting state that is
        // already applied (page defaults + this job's kan-78 geo prefill — see
        // the synchronous reset above) and resolve the job's own label through
        // the location service (kan-83). Issued from here, after the read has
        // concluded without applying a row, so a prefill resolution and a
        // saved-row apply can never race for the same job.
        const stored = res.data?.jobOrderRecSearchSettings;
        if (!stored) {
          resolveJobPrefill(activeJobRef.current);
          return;
        }
        const saved = sanitizeSavedSearchSettings(stored.settings);
        if (!saved) {
          console.warn('[candidate-recommendations] ignoring unreadable saved search settings');
          resolveJobPrefill(activeJobRef.current);
          return;
        }

        // A saved row carries its own center: applied as stored, never
        // re-resolved (kan-83). Editing the box resolves it again — the
        // restored label itself counts as already committed, so a no-edit
        // focus/blur over it must not re-resolve or re-fire the search.
        geoResolveSeqRef.current += 1;
        lastCommitTextRef.current = saved.filters.geoCenterLabel
          .trim()
          .slice(0, MAX_GEO_CENTER_CHARS);
        resolvedPrefillRef.current = null;
        setGeoResolve({
          status:
            saved.filters.lat !== null
              ? 'resolved'
              : saved.filters.geoCenterLabel
              ? 'noMatch'
              : 'idle',
          formattedAddress: null,
        });
        setFiltersState(saved.filters);
        setMustHave(saved.mustHave);
        setTopN(saved.topN);
        setSearch(saved.search);
        setView(saved.mode);
        setPage(1);
      })
      .catch(() => {
        // Silent fallback: the defaults + job-geo prefill are already applied
        // (the reset above ran before this read), so a failed settings read
        // never breaks job selection and never leaks another job's settings.
        // Same supersede/takeover guard as the success path: a recruiter who has
        // already committed their own center must not be interrupted by the
        // prefill resolution this failure would otherwise kick off.
        if (settingsFetchJobIdRef.current !== nextJobId || currentJobIdRef.current !== nextJobId) {
          return;
        }
        settingsFetchJobIdRef.current = null;
        resolveJobPrefill(activeJobRef.current);
      });
    // activeJob is read (jobGeoDefaults) but keyed on its id: a background
    // job-list refetch swaps object identity without changing the job, and
    // must neither re-read the row nor re-run the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id, apolloClient]);

  /**
   * Saves the current configuration against the selected job order (kan-80).
   * Fire-and-forget by design: an executed search must never block on (or fail
   * because of) this write, and the row is last-write-wins anyway.
   */
  const persistSearchSettings = useCallback(
    (mode: 'batch' | 'search'): void => {
      if (!jobId) return;
      // The recruiter just ran a search: their state is authoritative from here.
      settingsFetchJobIdRef.current = null;
      upsertSearchSettingsMutation({
        variables: {
          jobOrderId: jobId,
          settings: buildSearchSettingsPayload({ mode, topN, mustHave, filters, search }),
        },
      }).catch(() => {
        /* silent — saving settings must never disturb the search UX */
      });
    },
    [jobId, topN, mustHave, filters, search, upsertSearchSettingsMutation]
  );

  const selectCompany = (companyId: string | null): void => {
    setSelectedCompanyId(companyId);
    setJobIdState(null);
    setPage(1);
  };

  const selectJob = (nextJobId: string | null): void => {
    setJobIdState(nextJobId);
    setPage(1);
    // kan-119: the scope is per job order — a new job starts on All candidates.
    setPoolScopeState('all');
    setPoolInfo(null);
  };

  // Same reset for every OTHER way jobId moves (company switch, the auto-select
  // of a company's first job, a restored-but-stale id falling back): the
  // applicant pool belongs to exactly one job order and must never carry over.
  // selectJob above does it synchronously for the explicit pick; this effect
  // is the catch-all, and a no-op when the state is already reset.
  useEffect(() => {
    setPoolScopeState('all');
    setPoolInfo(null);
  }, [jobId]);

  const setPoolScope = (scope: PoolScope): void => {
    setPoolScopeState(scope);
    setPoolInfo(null);
    setPage(1);
  };

  // Every public setter below that touches persisted state also cancels a
  // pending saved-settings apply (kan-80): once the recruiter starts composing,
  // a load that resolves late must not replace what they typed.
  //
  // filterEditCount increments ONLY on recruiter-initiated filter changes
  // (setFilters / clearFilters), never on programmatic applies (job-geo
  // prefill, job-switch reset, saved-settings load). The page's search
  // auto-rerun keys on this counter so loading another user's saved settings
  // can never fire a search by itself — only the Search button (or a real
  // filter edit while a search is active) does.
  const setFilters = (patch: Partial<FilterState>): void => {
    cancelSavedSettingsApply();
    setFiltersState((prev) => ({ ...prev, ...patch }));
    setFilterEditCount((n) => n + 1);
    setPage(1);
  };

  const clearFilters = (): void => {
    // "Clear all" clears recruiter-chosen filters, not the job's own location:
    // it lands back on the selected job's default (its location at
    // DEFAULT_JOB_RADIUS_MI), dropping any manual center/radius. Rebuilt from
    // activeJob here rather than left to the prefill effect so the reset is a
    // single state transition. No job / no coordinates -> plain defaults.
    cancelSavedSettingsApply();
    geoResolveSeqRef.current += 1;
    const geo = jobGeoDefaultsResolved(activeJob);
    // The box lands back on the job's own label: treat it as committed so a
    // no-edit blur over it doesn't re-resolve (resolveJobPrefill below
    // overwrites this when the label still needs its first resolution).
    lastCommitTextRef.current = geo.geoCenterLabel;
    setFiltersState({ ...DEFAULT_FILTERS, ...geo });
    setFilterEditCount((n) => n + 1);
    setPage(1);

    if (activeJob && resolvedPrefillRef.current?.jobId === activeJob.id) {
      // The prefill's own formatted address — geoResolveRef may still hold a
      // recruiter commit this reset just reverted.
      setGeoResolve({
        status: 'resolved',
        formattedAddress: resolvedPrefillRef.current.formattedAddress,
      });
    } else if (geo.geoCenterLabel) {
      // The job's label was never resolved (a saved row applied on selection, so
      // the prefill resolution never ran) — resolve it now.
      resolveJobPrefill(activeJob);
    } else {
      setGeoResolve({ status: 'idle', formattedAddress: null });
    }
  };

  /**
   * kan-83 center resolution — the rail's location box drives the radius filter.
   * See RecPageContextValue.commitGeoCenter for the call contract.
   */
  const commitGeoCenter = (rawText: string): void => {
    const text = rawText.trim().slice(0, MAX_GEO_CENTER_CHARS);
    // Enter followed by blur is one intent, not two geocodes. An 'error' is the
    // one status worth retrying with the same text.
    if (text === lastCommitTextRef.current && geoResolveRef.current.status !== 'error') return;
    lastCommitTextRef.current = text;
    cancelSavedSettingsApply();
    const seq = ++geoResolveSeqRef.current;

    if (!text) {
      // An untouched empty box (no-location job, restored empty-geo row) is
      // not an edit: nothing to clear, so no state change, no search re-run,
      // no page reset.
      const current = filtersRef.current;
      if (
        current.lat === null &&
        current.lng === null &&
        current.geoCenterLabel === '' &&
        current.radiusMi >= MAX_RADIUS_MI
      ) {
        setGeoResolve({ status: 'idle', formattedAddress: null });
        return;
      }
      // Deliberate clear, so BOTH custom flags are set (same convention as the
      // radius chip's "Any distance"): otherwise the prefill effect, which
      // re-runs on the geoCenterCustom flip, would re-apply the job's 20-mile
      // default radius under a center the recruiter just deleted. No service
      // call — an empty box has nothing to resolve.
      setFiltersState((prev) => ({
        ...prev,
        lat: null,
        lng: null,
        geoCenterLabel: '',
        radiusMi: MAX_RADIUS_MI,
        geoCenterCustom: true,
        radiusCustom: true,
      }));
      setGeoResolve({ status: 'idle', formattedAddress: null });
      // Recruiter-initiated and the geo filter just changed (it is gone now).
      setFilterEditCount((n) => n + 1);
      setPage(1);
      return;
    }

    setGeoResolve({ status: 'resolving', formattedAddress: null });
    const jobAtIssue = currentJobIdRef.current;
    const isStale = (): boolean =>
      geoResolveSeqRef.current !== seq || currentJobIdRef.current !== jobAtIssue;

    apolloClient
      .query<{ resolveLocation: ResolvedLocation | null }>({
        query: RESOLVE_LOCATION,
        variables: { locationText: text },
      })
      .then((res) => {
        if (isStale()) return;
        const found = res.data?.resolveLocation ?? null;
        if (!found) {
          // No usable match — and the service cannot tell that apart from a
          // Google-side failure, so this deliberately does NOT fall back to the
          // job's stored coordinates.
          setFiltersState((prev) => ({ ...prev, lat: null, lng: null }));
          setGeoResolve({ status: 'noMatch', formattedAddress: null });
          setFilterEditCount((n) => n + 1);
          setPage(1);
          return;
        }
        setFiltersState((prev) => ({
          ...prev,
          lat: found.latitude,
          lng: found.longitude,
          geoCenterCustom: true,
          // A committed center must actually filter: lift a non-custom "Any
          // distance" off the sentinel to the job default, matching the prefill
          // success path. radiusCustom too, or the prefill effect re-runs on
          // the custom-flag flip and puts the sentinel back.
          ...(!prev.radiusCustom && prev.radiusMi >= MAX_RADIUS_MI
            ? { radiusMi: DEFAULT_JOB_RADIUS_MI, radiusCustom: true }
            : {}),
        }));
        setGeoResolve({ status: 'resolved', formattedAddress: found.formattedAddress });
        // Recruiter-initiated: an active search must re-rank on the new center.
        setFilterEditCount((n) => n + 1);
        setPage(1);
      })
      .catch(() => {
        if (isStale()) return;
        // The call failed (not "no match"): fall back to the job's stored
        // coordinates silently, which is the pre-kan-83 behavior.
        const fallback = jobGeoDefaults(activeJobRef.current);
        if (fallback.lat !== null && fallback.lng !== null) {
          setFiltersState((prev) => ({
            ...prev,
            lat: fallback.lat,
            lng: fallback.lng,
            geoCenterCustom: true,
            ...(!prev.radiusCustom && prev.radiusMi >= MAX_RADIUS_MI
              ? { radiusMi: DEFAULT_JOB_RADIUS_MI, radiusCustom: true }
              : {}),
          }));
          setGeoResolve({ status: 'resolved', formattedAddress: null });
          // The fallback is not this text's real resolution: forget the commit
          // so the same text can be retried once the service recovers (the
          // 'resolved' status would otherwise dedupe it forever).
          lastCommitTextRef.current = null;
        } else {
          setFiltersState((prev) => ({ ...prev, lat: null, lng: null }));
          setGeoResolve({ status: 'error', formattedAddress: null });
        }
        setFilterEditCount((n) => n + 1);
        setPage(1);
      });
  };

  const setViewAndResetPage = (nextView: TabKey): void => {
    cancelSavedSettingsApply();
    setView(nextView);
    setPage(1);
  };

  const setMustHaveTerms = (terms: string[]): void => {
    cancelSavedSettingsApply();
    setMustHave(terms);
  };

  // Changing the "Results (Top X)" count no longer re-fetches or resets the
  // result list (index.tsx reads topN via a ref, applying it on the next
  // Refresh/Search), so the current page is preserved too.
  const setTopNValue = (n: number): void => {
    cancelSavedSettingsApply();
    setTopN(n);
  };

  const setSearchMode = (mode: SearchMode): void => {
    cancelSavedSettingsApply();
    setSearch((prev) => ({ ...prev, mode }));
  };

  const setSearchQuery = (query: string): void => {
    cancelSavedSettingsApply();
    setSearch((prev) => ({ ...prev, query }));
  };

  const jobDescription = useMemo(
    () => stripHtml(activeJob?.advertisedJobDescription),
    [activeJob?.advertisedJobDescription]
  );

  const jobCenter = useMemo(
    () => ({ lat: filters.lat, lng: filters.lng, label: filters.geoCenterLabel }),
    [filters.lat, filters.lng, filters.geoCenterLabel]
  );

  const value: RecPageContextValue = {
    view,
    selectedCompanyId,
    jobId,
    jobDescription,
    jobCenter,
    topN,
    mustHave,
    filters,
    search,
    page,
    detailId,
    resumeViewId,
    ingest,

    setView: setViewAndResetPage,
    selectCompany,
    selectJob,
    setTopN: setTopNValue,
    setMustHave: setMustHaveTerms,
    setFilters,
    filterEditCount,
    clearFilters,
    commitGeoCenter,
    geoResolve,
    setSearchMode,
    setSearchQuery,
    setPage,
    setDetailId,
    setResumeViewId,
    setIngest,
    persistSearchSettings,

    poolScope,
    setPoolScope,
    poolInfo,
    setPoolInfo,

    companies,
    companiesLoading,
    jobs,
    jobsLoading,
    activeJob,

    dispositions,
    shortlistIds,
    declinedIds,
    shortlistCount: shortlistIds.length,
    declinedCount: declinedIds.length,
    refetchDispositions,
    shortlist,
    decline,
    restore,
    bulkApply,
  };

  return <RecPageContext.Provider value={value}>{children}</RecPageContext.Provider>;
};

export default RecPageContext;
